/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-final-form'
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays'
import FormHelperText from '@mui/material/FormHelperText'
import Typography from '@mui/material/Typography'
import { FieldValidator } from 'final-form'
import { ReactSortable } from 'react-sortablejs'
import TableButtons from 'components/TableButtons/TableButtons'
import ConfirmationModal from 'components/Modals/ConfirmationModal'
import { moveElementInArray } from 'utils/moveElementInArray'
import { AdvancedField, AdvancedFieldProps } from './AdvancedField'
import * as classes from './styles'

export type AdvancedFieldArrayProps<Item> = {
  name: string
  validate?: FieldValidator<Item[]>
  minCount?: number
  maxCount?: number
  itemName: string
  getEmptyItem: () => Item
  checkIsEmpty?: (item: Item) => boolean
  label?: string
  noItemsText?: string
  className?: string
  isSortable?: boolean
  renderField: AdvancedFieldProps<Item>['renderField']
  validateField?: AdvancedFieldProps<Item>['validate']
  classNameField?: AdvancedFieldProps<Item>['className']
  alwaysEditable?: AdvancedFieldProps<Item>['alwaysEditable']
  initialEditable?: boolean
  removeConfirmation?: boolean
}

export type AdvancedFieldArrayContentProps<Item> = Omit<AdvancedFieldArrayProps<Item>, 'validate'> & {
  fields: FieldArrayRenderProps<Item, HTMLDivElement>['fields']
  meta: FieldArrayRenderProps<Item, HTMLElement>['meta']
}

export function AdvancedFieldArrayContent<Item>({
  name,
  fields,
  meta,
  minCount,
  maxCount,
  itemName,
  getEmptyItem,
  checkIsEmpty = (item) => !item,
  label,
  noItemsText,
  isSortable,
  renderField,
  validateField,
  classNameField,
  alwaysEditable = false,
  initialEditable = false,
  removeConfirmation,
  className,
}: AdvancedFieldArrayContentProps<Item>) {
  const form = useForm()
  const [indexToRemove, setIndexToRemove] = useState<number | null>(null)
  const [fieldsState, setFieldsState] = useState<Array<{ editable: boolean; tempValue: Item | null; empty?: boolean }>>(
    (fields.value ?? []).map((value) => ({
      editable: alwaysEditable || initialEditable,
      tempValue: value,
      empty: checkIsEmpty(value),
    }))
  )
  const error = typeof meta.error === 'string' ? meta.error : undefined

  const sortedFields = (fields.value ?? []).map((value, index) => ({
    id: String(index),
    value,
  }))

  useEffect(() => {
    form.subscribe(
      ({ initialValues }) => {
        setFieldsState(
          (initialValues[name] ?? []).map((value: Item) => ({
            editable: alwaysEditable || initialEditable,
            tempValue: value,
            empty: checkIsEmpty(value),
          }))
        )
      },
      { initialValues: true }
    )
  }, [])

  const handleAdd = useCallback(() => {
    const newItem = getEmptyItem()
    fields.push(newItem)
    setFieldsState((prev) => [...prev, { editable: true, tempValue: newItem, empty: true }])
  }, [])

  const handleEditableChange = useCallback((value: boolean, index: number) => {
    setFieldsState((prev) => {
      const prevUpd = [...prev]
      prevUpd[index] = { ...prevUpd[index], editable: value, empty: false }
      return prevUpd
    })
  }, [])

  const handleTempValueChange = useCallback((value: Item | null, index: number) => {
    setFieldsState((prev) => {
      const prevUpd = [...prev]
      prevUpd[index] = { ...prevUpd[index], tempValue: value }
      return prevUpd
    })
  }, [])

  const handleSort = (newFieldArray: Array<{ id: string; chosen?: boolean }>) => {
    const chosenIndex = newFieldArray.findIndex(({ chosen }) => chosen !== undefined)

    if (chosenIndex !== -1) {
      const chosenId = newFieldArray[chosenIndex].id
      const previousIndex = sortedFields.findIndex(({ id }) => id === chosenId)

      if (previousIndex !== -1 && previousIndex !== chosenIndex) {
        fields.move(previousIndex, chosenIndex)
        setFieldsState((prev) => moveElementInArray(prev, previousIndex, chosenIndex))
      }
    }
  }

  const handleRemove = useCallback(
    (value: Item | null, index: number) => {
      if (removeConfirmation && value) {
        setIndexToRemove(index)
      } else {
        fields.remove(index)
        setFieldsState((prev) => {
          const prevUpd = [...prev]
          prevUpd.splice(index, 1)
          return prevUpd
        })
      }
    },
    [removeConfirmation, fields.remove]
  )

  const handleConfirm = useCallback(() => {
    if (indexToRemove !== null) {
      fields.remove(indexToRemove)
      setFieldsState((prev) => {
        const prevUpd = [...prev]
        prevUpd.splice(indexToRemove, 1)
        return prevUpd
      })
      setIndexToRemove(null)
    }
  }, [indexToRemove])

  const renderFields = () => {
    if (!fields.length) {
      return noItemsText && <div css={classes.empty}>{noItemsText}</div>
    }

    return fields.map((field, index) => (
      <AdvancedField
        key={field}
        name={field}
        index={index}
        removeDisabled={minCount !== undefined && (fields.length ?? 0) <= minCount}
        onRemove={handleRemove}
        empty={fieldsState[index]?.empty}
        editable={fieldsState[index]?.editable}
        onEditableChange={handleEditableChange}
        tempValue={fieldsState[index]?.tempValue}
        onTempValueChange={handleTempValueChange}
        renderField={renderField}
        alwaysEditable={alwaysEditable}
        validate={validateField}
        className={classNameField}
        isSortable={isSortable}
      />
    ))
  }

  return (
    <div className={className}>
      {!!label && <Typography css={classes.label}>{label}</Typography>}
      {isSortable ? (
        <ReactSortable list={sortedFields} setList={handleSort} css={classes.wrapper}>
          {renderFields()}
        </ReactSortable>
      ) : (
        <div css={classes.wrapper}>{renderFields()}</div>
      )}
      <TableButtons
        itemName={itemName}
        onAddItem={handleAdd}
        disabled={maxCount !== undefined && (fields.length ?? 0) >= maxCount}
      />
      {Boolean(meta.touched && error) && <FormHelperText error>{error}</FormHelperText>}
      <ConfirmationModal
        open={indexToRemove !== null}
        title={`Deleting ${itemName} #${(indexToRemove ?? 0) + 1}`}
        subtitle={`Are you sure you want to delete this ${itemName.toLowerCase()}?`}
        onClose={() => setIndexToRemove(null)}
        onConfirm={handleConfirm}
        confirmText="Delete"
        closeText="Cancel"
      />
    </div>
  )
}

export function AdvancedFieldArray<Item>({ name, validate, ...props }: AdvancedFieldArrayProps<Item>) {
  return (
    <FieldArray<Item> name={name} validate={validate}>
      {({ fields, meta }) => <AdvancedFieldArrayContent name={name} fields={fields} meta={meta} {...props} />}
    </FieldArray>
  )
}
